import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, PackageSearch, Thermometer, Clock, CheckCircle2, Circle, MapPin, AlertTriangle, Navigation } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import RouteMap from "./RouteMap";
import { Wordmark } from "./Logo";
import { WEATHER_ICON, sevText, sevBadge } from "../../lib/weather";
import { logError } from "../../lib/logError";
import { LOGOUT } from "../../constants/testIds";

export default function Track() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (tn) => {
    const num = (tn || "").trim();
    if (!num) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(num)}`);
      setResult(data);
    } catch (err) {
      setResult(null);
      toast.error(err.response?.data?.detail || "No shipment found.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shipments");
        setShipments(data);
        if (data[0]) { setQuery(data[0].tracking_number); lookup(data[0].tracking_number); }
      } catch (e) { logError("track.loadShipments", e); }
    })();
  }, [lookup]);

  const doLogout = async () => { await logout(); nav("/"); };

  const center = result ? [
    result.route.cities.reduce((s, c) => s + c.lat, 0) / result.route.cities.length,
    result.route.cities.reduce((s, c) => s + c.lng, 0) / result.route.cities.length,
  ] : [32, -100];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="track-logo"><Wordmark sizeClass="h-7" /></Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">Hi, {user?.name || "Customer"}</span>
            <button data-testid={LOGOUT.button} onClick={doLogout} className="inline-flex items-center gap-2 text-sm rounded-full border border-border px-4 py-2 hover:bg-secondary transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <span className="font-mono-x text-primary text-xs tracking-[0.35em]">FREIGHT TRACKING</span>
        <h1 className="mt-3 font-heading font-extrabold text-4xl md:text-5xl tracking-tighter">Where's my freight?</h1>

        <div className="mt-8 rounded-2xl border border-border glass p-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-3 rounded-xl border border-border bg-background/50 px-4">
              <PackageSearch className="w-5 h-5 text-muted-foreground" />
              <input data-testid="track-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup(query)}
                placeholder="Enter tracking number (e.g. CR-TOR-CAL-8842)" className="flex-1 bg-transparent py-3.5 outline-none text-sm" />
            </div>
            <button data-testid="track-submit" onClick={() => lookup(query)} disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-semibold px-8 py-3.5 hover:shadow-[0_0_30px_hsl(var(--primary)/0.4)] transition-shadow disabled:opacity-60">
              <Search className="w-4 h-4" /> {loading ? "Tracking..." : "Track"}
            </button>
          </div>
          {shipments.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground self-center mr-1">Your shipments:</span>
              {shipments.map((s) => (
                <button key={s.tracking_number} data-testid={`shipment-chip-${s.tracking_number}`} onClick={() => { setQuery(s.tracking_number); lookup(s.tracking_number); }}
                  className="font-mono-x text-xs rounded-full border border-border px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
                  {s.tracking_number}
                </button>
              ))}
            </div>
          )}
        </div>

        {result && (
          <div data-testid="track-result" className="mt-8 grid lg:grid-cols-2 gap-6">
            {/* Summary + map */}
            <div className="space-y-6">
              <div className="rounded-2xl border border-border glass p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-mono-x text-xs text-muted-foreground">TRACKING</div>
                    <div className="font-heading font-bold text-xl">{result.tracking_number}</div>
                    {result.driver_name && <div className="text-xs text-muted-foreground mt-0.5">Driver · {result.driver_name}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {result.trip_started && result.live_location && (
                      <span data-testid="track-live-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 px-2.5 py-1 text-xs font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
                      </span>
                    )}
                    <span className="rounded-full bg-primary/10 text-primary border border-primary/20 px-3 py-1 text-sm font-semibold">{result.status}</span>
                  </div>
                </div>
                {result.trip_started && result.live_eta && (
                  <div data-testid="track-live-eta" className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
                    <div className="flex items-center gap-2 text-emerald-500"><Navigation className="w-4 h-4" /><span className="text-sm">Arriving in</span></div>
                    <div className="text-right">
                      <div className="font-heading font-extrabold text-2xl text-emerald-500 leading-none">{result.live_eta.text}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">{result.live_eta.distance_km} km remaining · live</div>
                    </div>
                  </div>
                )}
                <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />Route</div><div className="font-medium mt-1">{result.origin} → {result.destination}</div></div>
                  <div><div className="text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" />ETA</div><div className="font-medium mt-1">{result.eta}</div></div>
                  <div><div className="text-muted-foreground flex items-center gap-1"><PackageSearch className="w-3.5 h-3.5" />Cargo</div><div className="font-medium mt-1">{result.cargo}</div></div>
                  <div><div className="text-muted-foreground flex items-center gap-1"><Thermometer className="w-3.5 h-3.5" />Temp</div><div className="font-medium mt-1">{result.temp}</div></div>
                </div>
                <div className={`mt-5 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${sevBadge(result.delay_hours >= 3 ? 2 : result.delay_hours >= 1 ? 1 : 0)}`}>
                  <AlertTriangle className="w-4 h-4" /> Weather impact: {result.impact}{result.delay_hours > 0 ? ` · est. +${result.delay_hours}h` : ""}
                </div>
              </div>
              <div className="rounded-2xl border border-border glass p-2">
                <RouteMap routes={[result.route]} currentIndexById={{ [result.route.id]: result.current_index }} live={result.live_location} height="360px" center={center} zoom={4} />
              </div>
            </div>

            {/* Timeline */}
            <div className="rounded-2xl border border-border glass p-6">
              <h3 className="font-heading font-bold text-lg mb-6">Shipment Timeline</h3>
              <div className="relative pl-8">
                <div className="absolute left-[7px] top-1 bottom-1 w-px bg-border" />
                <div className="space-y-6">
                  {result.timeline.map((t, i) => {
                    const Icon = WEATHER_ICON[t.weather.icon] || WEATHER_ICON.cloud;
                    return (
                      <div key={i} data-testid={`timeline-stop-${i}`} className="relative">
                        <span className="absolute -left-[29px] top-0.5">
                          {t.state === "done" ? <CheckCircle2 className="w-4 h-4 text-primary" />
                            : t.state === "current" ? <span className="block w-4 h-4 rounded-full bg-primary ring-4 ring-primary/20 animate-pulse" />
                            : <Circle className="w-4 h-4 text-muted-foreground" />}
                        </span>
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className={`font-medium ${t.state === "pending" ? "text-muted-foreground" : ""}`}>{t.label}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <Icon className={`w-3.5 h-3.5 ${sevText(t.weather.severity)}`} /> {t.weather.condition} · {t.weather.temp}°C
                            </div>
                          </div>
                          {t.state === "current" && <span className="font-mono-x text-[10px] tracking-widest text-primary">NOW</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
